import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/widgets/layout/ui/AppLayout'
import { SkeletonStack } from '@/shared/ui'
import { useAgenda } from '@/entities/agenda'
import { ScoreRing, SentimentIcon } from '@/entities/call/ui/OutcomeIcon'
import type { Action } from '@/shared/api/types'
import styles from './ui/AgendaPage.module.css'

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const

/**
 * Календарь дел.
 *
 * Дела берутся из отчётов: обещанный перезвон с конкретным временем.
 * Всё, что модель разобрала как «перезвоните когда-нибудь», сюда не
 * попадает — дело без даты нельзя ни поставить в календарь, ни
 * выполнить.
 */
export function AgendaPage() {
  const [month, setMonth] = useState(() => monthKey(new Date()))
  const [selected, setSelected] = useState(() => dayKey(new Date()))

  const agenda = useAgenda(month)

  const cells = useMemo(() => monthGrid(month), [month])
  const dayActions = (agenda.data?.actions ?? []).filter((action) => action.day === selected)
  const overdue = agenda.data?.overdue ?? []

  const monthTotal = Object.values(agenda.data?.days ?? {}).reduce((sum, n) => sum + n, 0)

  // Дни, где есть просроченное: клетка красится по ним, а не по общему
  // числу дел — просрочка важнее загруженности.
  const overdueDays = useMemo(
    () => new Set(overdue.map((action) => action.day)),
    [overdue],
  )

  // Ближайшее дело после выбранного дня — чтобы пустой день не был
  // тупиком.
  const upcoming = (agenda.data?.actions ?? []).find((action) => action.day > selected)

  return (
    <>
      <PageHeader title="Что сделать" />

      <div className={styles.layout}>
        <div className={styles.panel}>
          <div className={styles.head}>
            <span className={styles.title}>{monthName(month)}</span>
            {monthTotal > 0 && (
              <span className={styles.subtitle}>
                {monthTotal} {plural(monthTotal, 'дело', 'дела', 'дел')}
              </span>
            )}
            <span className={styles.headSpacer} />
            <div className={styles.nav}>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => setMonth(shiftMonth(month, -1))}
                aria-label="Предыдущий месяц"
              >
                <ChevronLeft />
              </button>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => setMonth(shiftMonth(month, 1))}
                aria-label="Следующий месяц"
              >
                <ChevronRight />
              </button>
            </div>
          </div>

          <div className={styles.weekdays}>
            {WEEKDAYS.map((name, i) => (
              <span key={name} className={`${styles.weekday} ${i >= 5 ? styles.weekend : ''}`}>
                {name}
              </span>
            ))}
          </div>

          <div className={styles.grid}>
            {cells.map((cell) => {
              const total = agenda.data?.days[cell.key] ?? 0

              return (
                <button
                  key={cell.key}
                  type="button"
                  className={[
                    styles.day,
                    cell.otherMonth ? styles.dayOther : '',
                    total > 0 && !cell.otherMonth ? styles.dayBusy : '',
                    overdueDays.has(cell.key) ? styles.dayOverdue : '',
                    cell.key === todayKey() ? styles.dayToday : '',
                    cell.key === selected ? styles.daySelected : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setSelected(cell.key)}
                  title={total > 0 ? `${total} ${plural(total, 'дело', 'дела', 'дел')}` : undefined}
                >
                  {cell.date.getDate()}

                  <span className={styles.dots}>
                    {/* До трёх дел — точками, дальше числом: четыре
                        точки в клетке уже не считаются взглядом. */}
                    {total > 0 && total <= 3 ? (
                      Array.from({ length: total }, (_, i) => (
                        <span
                          key={i}
                          className={`${styles.dot} ${overdueDays.has(cell.key) ? styles.dotOverdue : ''}`}
                        />
                      ))
                    ) : total > 3 ? (
                      <span className={styles.more}>{total}</span>
                    ) : null}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        <div>
          {overdue.length > 0 && (
            <div className={styles.panel} style={{ marginBottom: 'var(--space-4)' }}>
              <div className={styles.head}>
                <span className={styles.title}>Просрочено</span>
                <span className={styles.headSpacer} />
                <span className={`${styles.count} ${styles.countAlert}`}>{overdue.length}</span>
              </div>
              {overdue.map((action) => (
                <ActionRow key={action.callId} action={action} overdue />
              ))}
            </div>
          )}

          <div className={styles.panel}>
            <div className={styles.head}>
              <span className={styles.title}>{dayTitle(selected)}</span>
              <span className={styles.subtitle}>{daySubtitle(selected)}</span>
              <span className={styles.headSpacer} />
              {dayActions.length > 0 && (
                <span className={styles.count}>{dayActions.length}</span>
              )}
            </div>

            {agenda.isPending ? (
              <div style={{ padding: 'var(--space-4)' }}>
                <SkeletonStack rows={3} height={36} />
              </div>
            ) : dayActions.length > 0 ? (
              dayActions.map((action) => <ActionRow key={action.callId} action={action} />)
            ) : (
              <div className={styles.empty}>
                <span className={styles.emptyIcon}>
                  <EmptyIcon />
                </span>
                На этот день дел нет
                {upcoming && (
                  <button
                    type="button"
                    className={styles.emptyLink}
                    onClick={() => setSelected(upcoming.day)}
                  >
                    Ближайшее — {dayTitle(upcoming.day).toLowerCase()}, {upcoming.time}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

function ActionRow({ action, overdue = false }: { action: Action; overdue?: boolean }) {
  return (
    <Link to={`/calls/${action.callId}`} className={styles.action}>
      <span className={`${styles.stripe} ${overdue ? styles.stripeOverdue : ''}`} />
      <span className={styles.actionTime}>{action.time}</span>

      <span className={`${styles.actionIcon} ${overdue ? styles.overdueIcon : ''}`}>
        <PhoneIcon />
      </span>

      <span className={styles.actionBody}>
        <span className={styles.actionNote} title={action.note || action.outcomeLabel}>
          {noteText(action)}
        </span>
        {action.summary && action.note?.trim() && action.note.trim().toLowerCase() !== 'перезвонить' && (
          <span className={styles.actionSummary}>{action.summary}</span>
        )}
      </span>

      <span className={styles.actionMeta}>
        {overdue && <span className={styles.actionAge}>{age(action.at)}</span>}
        <span className={`${styles.actionOutcome} ${outcomeClass(action.outcome)}`}>
          {action.outcomeLabel}
        </span>
        <SentimentIcon sentiment={action.sentiment} />
      </span>

      <span className={styles.actionScore}>
        <ScoreRing score={action.score} success={action.score >= 60} />
      </span>
    </Link>
  )
}

interface Cell {
  key: string
  date: Date
  otherMonth: boolean
}

/**
 * Сетка месяца, начиная с понедельника.
 *
 * Дни соседних месяцев показываются приглушённо, а не пустыми клетками:
 * дело, назначенное на первое число, иначе выглядит оторванным от
 * последней недели предыдущего месяца.
 */
function monthGrid(month: string): Cell[] {
  const [year, mon] = month.split('-').map(Number)
  const first = new Date(year ?? 2026, (mon ?? 1) - 1, 1)

  // getDay(): воскресенье нулевое, а неделя у нас с понедельника.
  const shift = (first.getDay() + 6) % 7
  const start = new Date(first)
  start.setDate(first.getDate() - shift)

  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(start)
    date.setDate(start.getDate() + i)
    return {
      key: dayKey(date),
      date,
      otherMonth: date.getMonth() !== first.getMonth(),
    }
  })
}

function dayKey(date: Date): string {
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${mm}-${dd}`
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function todayKey(): string {
  return dayKey(new Date())
}

function shiftMonth(month: string, delta: number): string {
  const [year, mon] = month.split('-').map(Number)
  const date = new Date(year ?? 2026, (mon ?? 1) - 1 + delta, 1)
  return monthKey(date)
}

/** «август 2026 г.» → «Август 2026». */
function monthName(month: string): string {
  const [year, mon] = month.split('-').map(Number)
  const name = new Date(year ?? 2026, (mon ?? 1) - 1, 1).toLocaleDateString('ru-RU', {
    month: 'long',
  })
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`
}

/**
 * Заголовок дня — коротко.
 *
 * «Сегодня» и «Завтра» человек читает быстрее, чем дату, а для
 * остальных дней хватает числа с месяцем. День недели ушёл в подпись
 * рядом: вместе они не помещались и наезжали на счётчик.
 */
function dayTitle(day: string): string {
  const date = new Date(`${day}T00:00:00`)
  if (Number.isNaN(date.getTime())) return 'Дела'

  if (day === todayKey()) return 'Сегодня'

  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (day === dayKey(tomorrow)) return 'Завтра'

  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

/** День недели, а для сегодня и завтра — ещё и дата. */
function daySubtitle(day: string): string {
  const date = new Date(`${day}T00:00:00`)
  if (Number.isNaN(date.getTime())) return ''

  const weekday = date.toLocaleDateString('ru-RU', { weekday: 'short' })

  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)

  if (day === todayKey() || day === dayKey(tomorrow)) {
    return `${date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}, ${weekday}`
  }
  return weekday
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}

/**
 * Что написать первой строкой дела.
 *
 * Заметка модели, если она говорит что-то сверх очевидного. Иначе —
 * сводка разговора: итог всё равно виден пилюлей справа, и повторять
 * его словом «Перезвонить» в двух местах строки незачем.
 */
function noteText(action: Action): string {
  const note = action.note?.trim()
  const useful = note && note.toLowerCase() !== 'перезвонить'

  const text = useful ? note : action.summary?.trim() || 'Перезвонить'
  return action.phone ? `${action.phone} · ${text}` : text
}

/** Цвет пилюли итога: к чему привёл прошлый разговор. */
function outcomeClass(outcome: string): string {
  if (outcome === 'appointment' || outcome === 'callback' || outcome === 'interested') {
    return styles.outcomeWin ?? ''
  }
  if (outcome === 'refusal' || outcome === 'hangup' || outcome === 'no_interest') {
    return styles.outcomeLoss ?? ''
  }
  return ''
}

/** Насколько просрочено. Дни, а не дата: важно «сколько тянется». */
function age(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (days <= 0) return 'сегодня'
  if (days === 1) return '1 день'
  if (days < 5) return `${days} дня`
  return `${days} дней`
}

function EmptyIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <rect x="4" y="6.5" width="20" height="18" rx="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9.5 3.5V7.5M18.5 3.5V7.5M4 12H24" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M11 18L17 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M5 1.9L6.1 4.4L4.9 5.6C5.5 6.9 7.1 8.5 8.4 9.1L9.6 7.9L12.1 9V11.2C12.1 11.7 11.7 12.1 11.2 12.1C5.9 11.8 2.2 8.1 1.9 2.8C1.9 2.3 2.3 1.9 2.8 1.9H5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ChevronLeft() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M7.5 2.5L4 6L7.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ChevronRight() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
